import { useState, useEffect } from 'react'
import { Mail, Phone, MapPin, Send, CheckCircle, Users, Award, Leaf, Globe } from 'lucide-react'
import { teamAPI } from '../services/api'
import { getImageUrl } from '../utils/imageHelper'

const About = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [team, setTeam] = useState([])
  const [loadingTeam, setLoadingTeam] = useState(true)

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const fetchTeamMembers = async () => {
    try {
      const response = await teamAPI.getAll();
      setTeam(response.data);
    } catch (error) {
      console.error('Error fetching team members:', error);
    } finally {
      setLoadingTeam(false);
    }
  };

  const milestones = [
    { year: '1999', title: 'Company Founded', description: 'Alslam Aldawleia started with a vision to revolutionize sustainable agriculture' },
    { year: '2017', title: 'Africana Agriculture', description: 'Established state-of-the-art research facility' },
    { year: '2020', title: '1000+ Farmers Served', description: 'Reached milestone of supporting over 1000 farming operations' },
    { year: '2024', title: 'Al Rawabi Al Khader Investement & Trad LLC.', description: 'Achieved carbon neutrality across all manufacturing processes' },
    { year: '2025', title: 'Novatarra', description: 'Established in Latvia is specialized agriculture company focused on producing high-quality fertilizers and agriculture nutrients' }
  ]

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000))



      setSubmitSuccess(true)
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        subject: '',
        message: ''
      })
    } catch (error) {
      console.error('Error submitting form:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <section className="bg-white py-16">
        <div className="container-custom text-center">
          <h1 className="section-heading mb-4">About Novatarra</h1>
          <p className="subheading max-w-3xl">
            We are passionate about sustainable agriculture and helping farmers achieve
            better yields while protecting our environment for future generations.
          </p>
        </div>
      </section>

      {/* Company Story */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Story</h2>
              <div className="space-y-4 text-lg text-gray-700">
                <p>
                  Novatarra roots from the year 1999 the time Alslam Aldawleia was founded. Novatarra was established in the European Union (Latvia) 2025, Novatarra began with a simple yet powerful vision: to revolutionize agriculture through sustainable practices and innovative solutions. Our founders,
                  a team of agricultural scientists and environmental advocates, recognized the need
                  for products that could boost crop yields while protecting soil health and the environment.
                </p>
                <p>
                  Over the years, we've grown from a small research lab to a leading provider of
                  premium agricultural solutions, serving thousands of farmers worldwide. Our commitment
                  to sustainability and innovation has never wavered, and we continue to push the
                  boundaries of what's possible in sustainable agriculture.
                </p>
                <p>
                  Today, Novatarra stands as a trusted partner for farmers who want to grow better
                  crops while preserving the land for future generations. Our products are used
                  on farms of all sizes, from small family operations to large commercial enterprises.
                </p>
              </div>
            </div>
            <div>
              <div
                className="h-96 rounded-xl bg-cover bg-center"
                style={{
                  backgroundImage: "url('https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=800&h=600&fit=crop')"
                }}
              >
                <div className="h-full w-full bg-black bg-opacity-30 rounded-xl flex items-center justify-center">
                  <div className="text-center text-white">
                    <Leaf className="h-16 w-16 mx-auto mb-4" />
                    <h3 className="text-2xl font-bold">Growing the Future</h3>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section id="team" className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Meet Our Team</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Our dedicated team of agricultural experts, scientists, and sustainability advocates
              work together to bring you the best solutions for your farming needs.
            </p>
          </div>

          {loadingTeam ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-earth-green"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {team.map((member, index) => (
                <div key={index} className="bg-white p-6 rounded-xl text-center border border-gray-200">
                  <img
                    src={getImageUrl(member.image)}
                    alt={member.name}
                    className="w-32 h-32 rounded-full mx-auto mb-4 object-cover"
                  />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{member.name}</h3>
                  <p className="text-earth-green font-medium mb-3">{member.role}</p>
                  <p className="text-gray-600">{member.bio}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Milestones */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Journey</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Key milestones in our mission to revolutionize sustainable agriculture
            </p>
          </div>

          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-earth-green"></div>
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <div key={index} className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                  <div className={`w-1/2 ${index % 2 === 0 ? 'pr-8 text-right' : 'pl-8 text-left'}`}>
                    <div className="bg-white p-6 rounded-xl shadow-lg">
                      <div className="text-2xl font-bold text-earth-green mb-2">{milestone.year}</div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">{milestone.title}</h3>
                      <p className="text-gray-600">{milestone.description}</p>
                    </div>
                  </div>
                  <div className="w-8 h-8 bg-earth-green rounded-full border-4 border-white shadow-lg z-10"></div>
                  <div className="w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Get in Touch</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Have questions about our products or need agricultural advice?
              We're here to help you succeed.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-6">Contact Information</h3>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-earth-green p-3 rounded-lg">
                      <MapPin className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Address</h4>
                      <p className="text-gray-600">
                        Burtnieku 39, Riga LV-1084 Latvia
                      </p>
                    </div>
                  </div>

                  {1 > 2 ? <div className="flex items-start space-x-4">
                    <div className="bg-earth-green p-3 rounded-lg">
                      <Phone className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Phone</h4>
                      <p className="text-gray-600">(371) 123-4567</p>
                    </div>
                  </div> : null}


                  <div className="flex items-start space-x-4">
                    <div className="bg-earth-green p-3 rounded-lg">
                      <Mail className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Email</h4>
                      <p className="text-gray-600">info@novatarra.com</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-4">Business Hours</h4>
                <div className="space-y-2 text-gray-600">
                  <div className="flex justify-between">
                    <span>Monday - Friday</span>
                    <span>9:00 AM - 5:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Saturday</span>
                    <span>Closed</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Sunday</span>
                    <span>Closed</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">Send us a Message</h3>

              {
                <div className="bg-blue-50 border rounded-xl p-6 text-center">
                  <Mail className="h-12 w-12 text-green-600 mx-auto mb-4" />
                  <h4 className="text-lg font-semibold text-green-800 mb-2">info@novatarra.com</h4>
                  <p className="text-green-700">
                    You can contact us. We'll get back to you within 24 hours.
                  </p>
                </div>
              }
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default About
